import { chunk, groupBy } from 'lodash';
import {
	DPP_MAX_EVENTS_PER_BATCH,
	DPP_PROXY_BASE_PATH,
	DPP_TRACKED_ONCE_STORAGE_PREFIX,
} from '@/data/dpp/constants';
import { buildDppEventBatchDocument, DPP_EVENTS_PATH, sendDppEventBatch } from '@/data/dpp/client';
import { readDppJourneyId } from '@/data/dpp/journey';
import type { DppEventInput, DppSiteEvent } from '@/data/dpp/types';

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BUFFER = 100;

type BufferedEvent = { journeyId: string; event: DppSiteEvent };

let buffer: BufferedEvent[] = [];
let storeId = '';
let flushTimer: ReturnType<typeof setInterval> | null = null;
let trackedOnce = new Set<string>();

const takeBuffer = () => buffer.splice(0, buffer.length);

const toBatches = (items: BufferedEvent[]) =>
	Object.entries(groupBy(items, 'journeyId')).flatMap(([journeyId, group]) =>
		chunk(group, DPP_MAX_EVENTS_PER_BATCH).map((part) => ({
			journeyId,
			items: part,
			batch: { storeId, journeyId, events: part.map(({ event }) => event) },
		}))
	);

export const setDppTrackerStoreId = (next?: string) => {
	storeId = next ?? '';
};

export const flushDppEvents = async (): Promise<void> => {
	if (buffer.length === 0 || !storeId) return;

	const failed: BufferedEvent[] = [];

	await Promise.all(
		toBatches(takeBuffer()).map(async ({ items, batch }) => {
			try {
				await sendDppEventBatch(batch);
			} catch {
				failed.push(...items);
			}
		})
	);

	buffer = [...failed, ...buffer].slice(-MAX_BUFFER);
};

export const trackDppEvents = (inputs: DppEventInput[]): void => {
	const journeyId = readDppJourneyId();
	if (!journeyId || inputs.length === 0) return;

	const ts = new Date().toISOString();
	const items = inputs.map(({ type, partNumber, destination, source }) => ({
		journeyId,
		event: {
			type,
			ts,
			...(partNumber ? { partNumber } : {}),
			...(destination ? { destination } : {}),
			...(source ? { source } : {}),
		},
	}));
	buffer = [...buffer, ...items].slice(-MAX_BUFFER);

	if (buffer.length >= DPP_MAX_EVENTS_PER_BATCH) {
		void flushDppEvents();
	}
};

export const trackDppEvent = (input: DppEventInput): void => trackDppEvents([input]);

const wasTrackedBefore = (key: string) => {
	if (trackedOnce.has(key)) return true;
	try {
		return sessionStorage.getItem(key) !== null;
	} catch {
		return false;
	}
};

const markTracked = (key: string) => {
	trackedOnce.add(key);
	try {
		sessionStorage.setItem(key, new Date().toISOString());
	} catch {
		return;
	}
};

export const trackDppEventOnce = (input: DppEventInput, scope: string): void => {
	const journeyId = readDppJourneyId();
	if (!journeyId) return;

	const key = `${DPP_TRACKED_ONCE_STORAGE_PREFIX}${journeyId}:${input.type}:${scope}`;
	if (wasTrackedBefore(key)) return;

	markTracked(key);
	trackDppEvent(input);
};

export const beaconFlushDppEvents = (): void => {
	if (buffer.length === 0 || !storeId) return;

	if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
		void flushDppEvents();
		return;
	}

	const url = `${DPP_PROXY_BASE_PATH}/${DPP_EVENTS_PATH}`;
	toBatches(takeBuffer()).forEach(({ batch }) => {
		const body = JSON.stringify(buildDppEventBatchDocument(batch));
		navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
	});
};

export const startDppTracker = (): void => {
	if (flushTimer || typeof window === 'undefined') return;
	flushTimer = setInterval(() => void flushDppEvents(), FLUSH_INTERVAL_MS);
};

export const stopDppTracker = (): void => {
	if (!flushTimer) return;
	clearInterval(flushTimer);
	flushTimer = null;
};

export const dppTrackerBufferSize = (): number => buffer.length;

export const resetDppTracker = (): void => {
	buffer = [];
	storeId = '';
	trackedOnce = new Set();
	stopDppTracker();
};
